import React, { FC, useEffect, useState } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { useForm } from 'react-hook-form'

import {
    Button,
    Container,
    Form,
    Icon,
    Popover,
    SpaceBetween
} from '@cloudscape-design/components'

import FormInput from '../../ControlledFormInputs/Input'

import { useApprovalFormStore } from '../../../stores/applications/approvalFormStore'
import type { ApplicationApprovalRequest } from '../../../types/applications'
import useAcceptApplication from '../../../hooks/mutate/useAcceptApplication'
import FormMultiselect from '../../ControlledFormInputs/Multiselect'
import usePlatforms from '../../../hooks/query/usePlatforms'
import useApplications from '../../../hooks/query/useApplications'

interface ApprovalFormProps {
    thisApplicationID: string
    shouldShowApprovalForm: (shouldShow: boolean) => void
}
interface Option {
    label: string
    value: string
    description: string
}

const ApprovalForm: FC<ApprovalFormProps> = ({
    thisApplicationID,
    shouldShowApprovalForm
}) => {
    const { authState } = useOktaAuth()
    const thisUserEmail = authState?.idToken?.claims.email as string

    const {
        formValues,
        actions: { setFormValues, clearFormValues }
    } = useApprovalFormStore()

    useEffect(() => {
        const approvervalue = thisUserEmail
        setFormValues('approverID', approvervalue)
    }, [thisUserEmail, setFormValues])

    const {
        control,
        trigger,
        setValue,
        formState: { isValid }
    } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: formValues
    })
    
    const { data: platforms } = usePlatforms()
    const { data: applications } = useApplications()

    // --- EXTRACTED FOR UI RENDERING ---
    const selectedApplication = applications?.find(
        (application) => application.applicationID === thisApplicationID
    )
    const isNonGke = selectedApplication?.details?.infrastructureType === 'non-gke' || selectedApplication?.details?.platformID === 'custom'

    useEffect(() => {
        if (
            applications &&
            applications.length > 0 &&
            platforms &&
            platforms.length > 0
        ) {
            if (selectedApplication) {
                if (isNonGke) {
                    // --- NON-GKE LOGIC ---
                    const projectID = selectedApplication.details.gcpProjectID || 'N/A'
                    
                    let env = selectedApplication.details.environment?.value || selectedApplication.details.environment
                    if (env === 'staging') env = 'stg'

                    const hostname = `${selectedApplication.details.applicationName}.elz-gcp-${env}-globetel.com`

                    setValue('projectId', projectID)
                    setFormValues('projectId', projectID)
                    
                    // --- SMART WORKLOAD CLUSTER ALIASING ---
                    let wkldName = 'Non-GKE (Custom)' // Default for standalone GCP projects
                    
                    // If platformID is NOT 'custom', they associated it with an existing platform!
                    if (selectedApplication.details.platformID && selectedApplication.details.platformID !== 'custom') {
                        const filteredPlatforms = platforms.filter(
                            (platform) => platform.platformID === selectedApplication.details.platformID
                        )
                        if (filteredPlatforms.length > 0) {
                            const platformData: any = filteredPlatforms[0]
                            // Inherit the exact prefix from the parent platform
                            wkldName = platformData.namePrefix || platformData.platformName || 'Non-GKE (Custom)'
                        }
                    }

                    setValue('wkldClusterName', wkldName)
                    setFormValues('wkldClusterName', wkldName)

                    if (!selectedApplication.details.additionalEnvName) {
                        setValue('hostname', hostname)
                        setFormValues('hostname', hostname)
                    }

                } else {
                    // --- STANDARD GKE LOGIC ---
                    const platformIdTarget = selectedApplication.details.platformName?.value || selectedApplication.details.platformID
                    
                    const filteredPlatforms = platforms.filter(
                        (platform) => platform.platformID === platformIdTarget
                    )
                    
                    if (filteredPlatforms.length > 0) {
                        const platformData: any = filteredPlatforms[0]
                        const projectID = platformData.gcpProjectID

                        let env = platformData.environment
                        if (env === 'staging') env = 'stg'

                        const hostname = `${selectedApplication.details.applicationName}.elz-gcp-${env}-globetel.com`

                        setValue('projectId', projectID)
                        setFormValues('projectId', projectID)
                        
                        const wkldClusterName = platformData.namePrefix
                        setValue('wkldClusterName', wkldClusterName)
                        setFormValues('wkldClusterName', wkldClusterName)

                        if (!selectedApplication.details.additionalEnvName) {
                            setValue('hostname', hostname)
                            setFormValues('hostname', hostname)
                        }
                    }
                }
            }
            setValue('region', 'Singapore (asia-southeast1)')
        }
    }, [thisApplicationID, platforms, applications, setValue, setFormValues, selectedApplication, isNonGke])

    const { mutate: acceptApplication, isLoading } =
        useAcceptApplication(thisApplicationID)

    const _handleApprove = () => {
        trigger()

        if (!isValid) return

        const approvalRequest: ApplicationApprovalRequest = {
            action: 'APPROVE',
            applicationID: thisApplicationID,
            approverID: thisUserEmail,
            details: formValues
        }

        acceptApplication(approvalRequest, {
            onSuccess: () => {
                shouldShowApprovalForm(false)
                clearFormValues()
                window.location.reload()
            }
        })
    }

    return (
        <Form
            actions={
                <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={() => shouldShowApprovalForm(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        loading={isLoading}
                        onClick={_handleApprove}
                    >
                        Approve
                    </Button>
                </SpaceBetween>
            }
        >
            <Container>
                <SpaceBetween direction="vertical" size="l">
                    <div style={{ maxWidth: 550 }}>
                        <FormInput
                            name="region"
                            label="Region"
                            type="text"
                            control={control}
                            placeholder="Select a region"
                            setFormValues={setFormValues}
                            disabled={true}
                        />
                    </div>
                    <div style={{ maxWidth: 550 }}>
                        <FormInput
                            name="projectId"
                            label="Workload Project ID"
                            type="text"
                            control={control}
                            setFormValues={setFormValues}
                            placeholder=""
                            disabled={true}
                        />
                    </div>
                    <div style={{ maxWidth: 550 }}>
                        <FormInput
                            name="wkldClusterName"
                            // SMART LABEL: Switches based on infrastructure type
                            label={isNonGke ? 'Workload Name' : 'Workload Cluster Name'}
                            type="text"
                            control={control}
                            setFormValues={setFormValues}
                            placeholder=""
                            disabled={true}
                        />
                    </div>
                </SpaceBetween>
            </Container>
        </Form>
    )
}

export default ApprovalForm